import { JSX, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { getDashboardWidgetDataApi } from "src/api/dashboardApi";

type LimitOption = { value: number | string; label: string };

type MetaFilter = {
  key: string;
  label?: string;
  default?: number | string;
  options?: LimitOption[];
};

type Payer = {
  payer: string;
  display_payer: string;
  approval_pct: number;
  decided: number;
  volume: number;
  small_sample: boolean;
};

type CardItem = {
  name: string;
  value: number;
  percentage: number;
  rule: string;
  summary: string;
  payers: Payer[];
  color: string;
};

type Metadata = {
  subtitle?: string;
  total_payers?: number;
  payers_counted?: number;
  methodology?: string;
  filters?: MetaFilter[];
  [key: string]: unknown;
};

type CardData = {
  data?: CardItem[];
  metadata?: Metadata;
};

type CardWidgetProps = {
  isLoading: boolean;
  data: unknown;
  dashboardName?: string;
  widgetTemplateId?: string | number;
  appliedFilters?: Record<string, unknown>;
};

const FALLBACK_LIMIT_OPTIONS: LimitOption[] = [
  { value: 10, label: "Top 10" },
  { value: 20, label: "Top 20" },
  { value: 50, label: "Top 50" },
  { value: "all", label: "All" },
];

const unwrap = (raw: unknown): CardData => {
  if (!raw || typeof raw !== "object") return {};
  const obj = raw as Record<string, unknown>;
  if (Array.isArray(obj.data)) return obj as CardData;
  return obj as CardData;
};

export default function CardWidget({
  isLoading,
  data,
  dashboardName,
  widgetTemplateId,
  appliedFilters,
}: CardWidgetProps): JSX.Element {
  const [limit, setLimit] = useState<number | string | null>(null);

  const canFetch =
    !!dashboardName &&
    !!widgetTemplateId &&
    !!appliedFilters &&
    Object.keys(appliedFilters).length > 0;

  const { data: queryData, isLoading: isFetching } = useQuery({
    queryKey: [
      "widget",
      dashboardName,
      widgetTemplateId,
      appliedFilters,
      limit,
    ],
    queryFn: () =>
      getDashboardWidgetDataApi(dashboardName!, String(widgetTemplateId!), {
        filters: appliedFilters,
        widget_filters: limit === null ? undefined : { top_n: limit },
      }),
    enabled: canFetch,
    staleTime: 0,
    gcTime: 0,
  });

  const parsed = useMemo(
    () => unwrap(canFetch ? queryData : data),
    [canFetch, queryData, data],
  );
  const cards = parsed.data ?? [];
  const meta = parsed.metadata;

  const topNFilter = meta?.filters?.find((f) => f.key === "top_n");
  const limitOptions = topNFilter?.options?.length
    ? topNFilter.options
    : FALLBACK_LIMIT_OPTIONS;
  const limitValue = limit ?? topNFilter?.default ?? 10;
  const counted = meta?.payers_counted ?? limitValue;

  if (canFetch ? isFetching : isLoading) {
    return (
      <div className="placeholder-glow h-100">
        <span className="placeholder w-100 h-100" />
      </div>
    );
  }

  const limitSelect = canFetch ? (
    <select
      className="form-select form-select-sm"
      style={{
        width: "auto",
        fontSize: 11,
        minWidth: 72,
        padding: "1px 24px 1px 6px",
        cursor: "pointer",
        flexShrink: 0,
      }}
      value={String(limitValue)}
      onChange={(e) => {
        const v = e.target.value;
        setLimit(v === "all" ? v : Number(v));
      }}
      aria-label="How many payers to show"
    >
      {limitOptions.map((o) => (
        <option key={String(o.value)} value={String(o.value)}>
          {o.label}
        </option>
      ))}
    </select>
  ) : null;

  if (cards.length === 0) {
    return (
      <div className="card-widget">
        <p className="text-muted small mb-0">No data available.</p>
      </div>
    );
  }

  return (
    <div className="card-widget">
      <div className="card-widget__meta">
        {meta?.subtitle && (
          <span className="card-widget__subtitle">{meta.subtitle}</span>
        )}
        {limitSelect}
      </div>

      <div className="card-widget__grid">
        {cards.map((card) => (
          <div key={card.name} className="card-widget__card">
            <div className="card-widget__card-name">{card.name}</div>
            <div
              className="card-widget__card-value"
              style={{ color: card.color }}
            >
              {card.value}
            </div>
            <div className="card-widget__card-pct">
              {card.percentage}% of top-{counted} payers
            </div>
            <div className="card-widget__card-summary">{card.summary}</div>
            <div
              className="card-widget__card-accent"
              style={{ background: card.color }}
            />
          </div>
        ))}
      </div>

      {meta?.methodology && (
        <div className="card-widget__methodology">
          <strong>Access tier methodology:</strong>{" "}
          {meta.methodology.replace(/^Access tier methodology:\s*/i, "")}
        </div>
      )}
    </div>
  );
}
